import { useApp } from '../context';
import { useEffect, useMemo, useRef, useState } from 'react';
import { PageHub } from '../components/PageHub';
import { iptv, type CalEpisode, type CalMovie, type ExtItem, type SportEvent } from '../api';
import { dayName, fullDate, loadCalendar, RELEASE_LABEL, todayYmd, useCalendar } from '../calendar';
import { linkProps } from '../router';
import { PlayIcon } from '../components/Icons';
import { RequestDialog } from '../components/Widgets';
import { ChannelLogo, ChannelName, LiveBadge, playLink } from '../components/LiveBits';
import { hasReminder, toggleReminder, useReminders } from '../reminders';

/**
 * Kalender: per dag de nieuwe afleveringen van je series en de releases van films die je
 * hebt aangevraagd of op je Trakt-watchlist staan. Vandaag bovenaan, de afgelopen week onderaan.
 */

type Tab = 'all' | 'tv' | 'movies' | 'sport';
type Entry = { kind: 'ep'; date: string; e: CalEpisode } | { kind: 'movie'; date: string; m: CalMovie } | { kind: 'sport'; date: string; s: SportEvent };

function ymdOf(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Sport voor de kalender: je clubs, F1 en Nederlandse wedstrijden; de rest staat bij Live TV. */
function useSport(): SportEvent[] {
  const [events, setEvents] = useState<SportEvent[]>([]);
  useEffect(() => {
    iptv.accounts()
      .then((a) => (a.accounts.some((x) => x.enabledForProfile) ? iptv.sport(168) : null))
      .then((r) => setEvents((r?.events ?? []).filter((e) => e.favorite || e.sport === 'f1' || e.nl)))
      .catch(() => setEvents([]));
  }, []);
  return events;
}

const TAB_KEY = 'tumedia.calendarTab';

function savedTab(): Tab {
  try {
    const v = localStorage.getItem(TAB_KEY);
    return v === 'tv' || v === 'movies' || v === 'sport' ? v : 'all';
  } catch {
    return 'all';
  }
}

export function Calendar() {
  const { data, error } = useCalendar();
  const [tab, setTab] = useState<Tab>(savedTab);
  const [requesting, setRequesting] = useState<ExtItem | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [fresh, setFresh] = useState<typeof data>(null);
  const cal = fresh ?? data;
  const sport = useSport();
  const [view, setView] = useState<'list' | 'month'>(() => {
    try { return localStorage.getItem('tumedia.calendarView') === 'month' ? 'month' : 'list'; } catch { return 'list'; }
  });
  const chooseView = (v: 'list' | 'month') => {
    setView(v);
    try { localStorage.setItem('tumedia.calendarView', v); } catch { /* niet erg */ }
  };

  const choose = (t: Tab) => {
    setTab(t);
    try {
      localStorage.setItem(TAB_KEY, t);
    } catch {
      // niet erg
    }
  };

  const refresh = () => {
    setRefreshing(true);
    loadCalendar(true).then(setFresh).catch(() => undefined).finally(() => setRefreshing(false));
  };

  const { upcoming, past } = useMemo(() => {
    const today = todayYmd();
    const entries: Entry[] = [];
    if (cal && (tab === 'all' || tab === 'tv')) for (const e of cal.episodes) entries.push({ kind: 'ep', date: e.date, e });
    if (cal && (tab === 'all' || tab === 'movies')) for (const m of cal.movies) entries.push({ kind: 'movie', date: m.date, m });
    if (tab === 'all' || tab === 'sport') for (const x of sport) entries.push({ kind: 'sport', date: ymdOf(x.start), s: x });
    const order = { sport: 0, ep: 1, movie: 2 } as const;
    const group = (list: Entry[]) => {
      const map = new Map<string, Entry[]>();
      for (const x of list) map.set(x.date, [...(map.get(x.date) ?? []), x]);
      return [...map.entries()];
    };
    const sorted = entries.sort((a, b) => a.date.localeCompare(b.date) || order[a.kind] - order[b.kind] || (a.kind === 'sport' && b.kind === 'sport' ? a.s.start - b.s.start : 0));
    return {
      upcoming: group(sorted.filter((x) => x.date >= today)),
      past: group(sorted.filter((x) => x.date < today).reverse()),
    };
  }, [cal, tab, sport]);

  const first = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (cal) first.current?.querySelector<HTMLElement>('a, button')?.focus({ preventScroll: true });
  }, [cal]);

  return (
    <main className="page calendar-page">
      <PageHub page="calendar" />
      <header className="page-head">
        <h1>Kalender</h1>
        {cal && <span className="subtle">{dayName(cal.from)} t/m {fullDate(cal.to)}</span>}
        <button className="row-more" onClick={refresh} disabled={refreshing}>{refreshing ? 'Verversen…' : 'Verversen'}</button>
      </header>

      <div className="filterbar" role="tablist" aria-label="Wat tonen" data-focus-group>
        {([['all', 'Alles'], ['tv', 'Series'], ['movies', 'Films'], ['sport', 'Sport']] as const).map(([id, label]) => (
          <button key={id} role="tab" className="chip" aria-selected={tab === id} aria-pressed={tab === id} onClick={() => choose(id)}>{label}</button>
        ))}
        <span className="chip-sep" />
        <button className="chip" aria-pressed={view === 'list'} onClick={() => chooseView('list')}>Lijst</button>
        <button className="chip" aria-pressed={view === 'month'} onClick={() => chooseView('month')}>Maand</button>
      </div>

      {error && <p className="placeholder-row error" role="alert">{error}</p>}
      {!cal && !error && (
        <p className="placeholder-row" aria-busy="true">Kalender opbouwen… De eerste keer kan dit even duren: alle series in je bibliotheek worden bij TMDB nagekeken.</p>
      )}
      {cal && !cal.sources.tmdb && <p className="placeholder-row">Voor de kalender is een TMDB-sleutel nodig. De eigenaar stelt die in bij Instellingen → Koppelingen.</p>}

      {cal && upcoming.length === 0 && past.length === 0 && cal.sources.tmdb && (
        <p className="placeholder-row">
          {tab === 'sport'
            ? 'Geen wedstrijden van je clubs, F1 of Nederlandse competities in de gids. Kies favoriete clubs bij Live TV.'
            : tab === 'movies'
            ? 'Geen films in aantocht. Zet films op Mijn lijst (of vraag ze aan via Verzoeken), dan zie je hier wanneer ze uitkomen.'
            : 'De komende weken geen nieuwe afleveringen. Zet series op Mijn lijst, of kijk ze via je addons, dan verschijnen ze hier.'}
        </p>
      )}

      {view === 'month' && cal && <MonthView days={[...past, ...upcoming]} onRequest={setRequesting} />}

      {view === 'list' && upcoming.map(([date, list], i) => (
        <DaySection key={date} date={date} list={list} onRequest={setRequesting} sectionRef={i === 0 ? (el) => { first.current = el; } : undefined} />
      ))}

      {view === 'list' && past.length > 0 && (
        <>
          <h2 className="cal-past-title">Afgelopen week</h2>
          {past.map(([date, list]) => <DaySection key={date} date={date} list={list} onRequest={setRequesting} past />)}
        </>
      )}

      {cal && (
        <p className="subtle cal-sources">
          Bronnen: {cal.sources.library ? `${cal.sources.library} series in je bibliotheek, ` : ''}Mijn lijst, wat je via addons kijkt
          {cal.sources.trakt ? ', je Trakt-kalender' : ''}
          {cal.sources.seerr ? ', je verzoeken in Seerr' : ''}.
        </p>
      )}
      <div className="footer-space" />
      {requesting && <RequestDialog item={requesting} onClose={() => setRequesting(null)} />}
    </main>
  );
}

const WEEKDAYS = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];

/** Maandoverzicht met artwork per dag; een dag kiezen toont die dag eronder. */
function MonthView({ days, onRequest }: { days: [string, Entry[]][]; onRequest: (x: ExtItem) => void }) {
  const byDate = useMemo(() => new Map(days), [days]);
  const today = todayYmd();
  const [month, setMonth] = useState(() => today.slice(0, 7));
  const [picked, setPicked] = useState(today);
  const [y, m] = month.split('-').map(Number);
  const first = new Date(y, m - 1, 1);
  const startDow = (first.getDay() + 6) % 7; // maandag = 0
  const count = new Date(y, m, 0).getDate();
  const cells: (string | null)[] = [...Array(startDow).fill(null), ...Array.from({ length: count }, (_x, i) => `${month}-${String(i + 1).padStart(2, '0')}`)];
  while (cells.length % 7) cells.push(null);
  const shift = (d: number) => {
    const n = new Date(y, m - 1 + d, 1);
    setMonth(`${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`);
  };
  const art = (list: Entry[]) => {
    for (const x of list) {
      if (x.kind === 'ep') return x.e.still ?? x.e.show.backdrop ?? x.e.show.poster;
      if (x.kind === 'movie') return x.m.backdrop ?? x.m.poster;
    }
    return null;
  };
  const label = first.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
  const list = byDate.get(picked);
  return (
    <section className="cal-month" aria-label={`Maand ${label}`}>
      <div className="cal-month-head">
        <button className="icon-btn" onClick={() => shift(-1)} aria-label="Vorige maand">‹</button>
        <h2>{label}</h2>
        <button className="icon-btn" onClick={() => shift(1)} aria-label="Volgende maand">›</button>
      </div>
      <div className="cal-grid" role="grid" data-focus-group>
        {WEEKDAYS.map((d) => <span key={d} className="cal-wd" role="columnheader">{d}</span>)}
        {cells.map((d, i) => {
          if (!d) return <span key={`x${i}`} className="cal-cell is-empty" />;
          const items = byDate.get(d) ?? [];
          const img = art(items);
          return (
            <button
              key={d}
              className={`cal-cell${d === today ? ' is-today' : ''}${d === picked ? ' is-picked' : ''}${d < today ? ' is-past' : ''}`}
              style={img ? { backgroundImage: `linear-gradient(transparent 35%, rgba(0,0,0,.85)), url("${img}")` } : undefined}
              onClick={() => setPicked(d)}
              aria-label={`${dayName(d, true)}: ${items.length ? `${items.length} items` : 'niets'}`}
              aria-pressed={d === picked}
            >
              <span className="cal-num">{Number(d.slice(8))}</span>
              {items.length > 0 && <span className="cal-count">{items.length > 1 ? `+${items.length}` : items[0].kind === 'ep' ? items[0].e.show.title : items[0].kind === 'movie' ? items[0].m.title : items[0].s.title}</span>}
            </button>
          );
        })}
      </div>
      {list ? <DaySection date={picked} list={list} onRequest={onRequest} past={picked < today} /> : <p className="subtle cal-none">Op {dayName(picked, true).toLowerCase()} staat niets in je kalender.</p>}
    </section>
  );
}

function DaySection({ date, list, past = false, onRequest, sectionRef }: {
  date: string;
  list: Entry[];
  past?: boolean;
  onRequest: (x: ExtItem) => void;
  sectionRef?: (el: HTMLElement | null) => void;
}) {
  const today = date === todayYmd();
  const label = dayName(date, true);
  const sub = ['Vandaag', 'Morgen', 'Gisteren'].includes(label) ? fullDate(date) : '';
  return (
    <section ref={sectionRef} className={`cal-day ${today ? 'is-today' : ''} ${past ? 'is-past' : ''}`} aria-label={`${label}${sub ? `, ${sub}` : ''}`}>
      <h2 className="cal-day-head">
        <span className="cal-day-name">{label}</span>
        {sub && <span className="subtle">{sub}</span>}
        <span className="subtle cal-count">{list.length}</span>
      </h2>
      <div className="cal-grid" data-focus-group>
        {list.map((x) => (x.kind === 'ep'
          ? <EpisodeCard key={x.e.id} e={x.e} past={past} onRequest={onRequest} />
          : x.kind === 'movie' ? <MovieCard key={x.m.id} m={x.m} onRequest={onRequest} />
          : <SportCard key={x.s.id} e={x.s} />))}
      </div>
    </section>
  );
}

function time(ms: number | null): string | null {
  return ms ? new Date(ms).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' }) : null;
}

function EpisodeCard({ e, past, onRequest }: { e: CalEpisode; past: boolean; onRequest: (x: ExtItem) => void }) {
  const noPlex = useApp().me.plex === false;
  const img = e.still ?? e.show.backdrop;
  const t = time(e.airTime);
  let status: { label: string; tone: string };
  if (e.ratingKey) status = { label: 'Nu te zien', tone: 'own' };
  else if (noPlex) status = past || e.date === todayYmd() ? { label: 'Uit', tone: 'own' } : { label: e.premiere ? (e.episode === 1 && e.season === 1 ? 'Nieuwe serie' : 'Nieuw seizoen') : 'Verwacht', tone: e.premiere ? 'premiere' : 'date' };
  else if (!e.show.ratingKey) status = { label: 'Niet in bibliotheek', tone: 'req' };
  else if (past || e.date === todayYmd()) status = { label: 'Nog niet binnen', tone: 'wait' };
  else status = { label: e.premiere ? (e.episode === 1 && e.season === 1 ? 'Nieuwe serie' : 'Nieuw seizoen') : 'Verwacht', tone: e.premiere ? 'premiere' : 'date' };

  const body = (
    <>
      <span className="cal-art" style={img ? { backgroundImage: `url("${img}")` } : undefined} aria-hidden="true">
        {!img && <span className="card-fallback">{e.show.title}</span>}
        <span className={`lib-badge ${status.tone}`}>{status.label}</span>
        {e.ratingKey && <span className="cal-play"><PlayIcon /></span>}
      </span>
      <span className="cal-body">
        <span className="cal-show">{e.show.title}</span>
        <span className="cal-ep">S{e.season} E{e.episode} · {e.title}</span>
        <span className="card-sub">{[t, e.source === 'trakt' ? 'via Trakt' : e.source === 'list' ? 'op je lijst' : e.source === 'watching' ? 'je kijkt dit' : null].filter(Boolean).join(' · ')}</span>
      </span>
    </>
  );
  const label = `${e.show.title}, seizoen ${e.season} aflevering ${e.episode}: ${e.title}. ${status.label}`;
  if (e.ratingKey) return <a className="cal-card" {...linkProps(`/item/${e.ratingKey}`)} aria-label={label}>{body}</a>;
  if (e.show.ratingKey) return <a className="cal-card" {...linkProps(`/item/${e.show.ratingKey}`)} aria-label={label}>{body}</a>;
  return (
    <button
      className="cal-card"
      aria-label={`${label}.${noPlex ? '' : ' Aanvragen'}`}
      onClick={() => onRequest({ type: 'tv', tmdbId: e.show.tmdbId, title: e.show.title, year: null, overview: null, poster: e.show.poster, backdrop: e.show.backdrop, ratingKey: null, ...(past || e.date <= todayYmd() ? { season: e.season, episode: e.episode, epTitle: e.title } : {}) })}
    >
      {body}
    </button>
  );
}

function MovieCard({ m, onRequest }: { m: CalMovie; onRequest: (x: ExtItem) => void }) {
  const noPlex = useApp().me.plex === false;
  const status = m.ratingKey
    ? { label: 'In bibliotheek', tone: 'own' }
    : m.source === 'seerr' ? { label: 'Aangevraagd', tone: 'date' } : m.source === 'list' || noPlex ? { label: 'Op je lijst', tone: 'req' } : { label: 'Op je watchlist', tone: 'req' };
  const body = (
    <>
      <span className="cal-art cal-art-movie" style={m.backdrop ? { backgroundImage: `url("${m.backdrop}")` } : undefined} aria-hidden="true">
        {m.poster && <img className="cal-poster" src={m.poster} alt="" loading="lazy" />}
        <span className={`lib-badge ${status.tone}`}>{status.label}</span>
      </span>
      <span className="cal-body">
        <span className="cal-show">{m.title}</span>
        <span className="cal-ep">{RELEASE_LABEL[m.kind]}</span>
        <span className="card-sub">{['Film', m.year].filter(Boolean).join(' · ')}</span>
      </span>
    </>
  );
  const label = `${m.title}, ${RELEASE_LABEL[m.kind].toLowerCase()}. ${status.label}`;
  if (m.ratingKey) return <a className="cal-card" {...linkProps(`/item/${m.ratingKey}`)} aria-label={label}>{body}</a>;
  if (m.source === 'seerr') return <a className="cal-card" {...linkProps(`/requests?q=${encodeURIComponent(m.title)}`)} aria-label={label}>{body}</a>;
  return (
    <button className="cal-card" aria-label={`${label}.${noPlex ? '' : ' Aanvragen'}`} onClick={() => onRequest({ type: 'movie', tmdbId: m.tmdbId, title: m.title, year: m.year, overview: m.overview, poster: m.poster, backdrop: m.backdrop, ratingKey: null })}>
      {body}
    </button>
  );
}

/** Wedstrijd of F1-sessie: tijd, zender, en kijken of een herinnering zetten. */
function SportCard({ e }: { e: SportEvent }) {
  useReminders();
  const ch = e.channels[0];
  const reminded = hasReminder(e.id);
  const t = new Date(e.start).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
  const label = e.sport === 'f1' ? `Formule 1${e.session ? ` · ${e.session}` : ''}` : e.competition ?? (e.sport === 'football' ? 'Voetbal' : 'Sport');
  return (
    <div className={`cal-card cal-sport ${e.sport}`}>
      <a className="cal-sport-main" {...linkProps(playLink(ch.accountId, ch.streamId))} aria-label={`${e.title}, ${label}, ${e.live ? 'nu live' : t} op ${ch.name}`}>
        <span className="cal-sport-top">
          {e.live ? <LiveBadge /> : <span className="cal-sport-time">{t}</span>}
          <span className="sport-comp">{label}{e.favorite ? ' ★' : ''}</span>
        </span>
        <span className="cal-show">{e.title}</span>
        <span className="cal-sport-ch">
          <ChannelLogo src={ch.logo} name={ch.name} size={22} />
          <ChannelName name={ch.name} />
          {e.channels.length > 1 && <span className="subtle">+{e.channels.length - 1}</span>}
        </span>
      </a>
      {!e.live && (
        <button
          className="chip cal-sport-bell"
          aria-pressed={reminded}
          onClick={() => toggleReminder({ id: e.id, title: e.title, subtitle: label, start: e.start, accountId: ch.accountId, streamId: ch.streamId })}
        >
          {reminded ? 'Herinnering aan' : 'Herinner mij'}
        </button>
      )}
    </div>
  );
}
